import { computed, DestroyRef, effect, inject, Injectable, type Signal, signal, untracked } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';

import { ProjectStage } from '@efficiently/models';

import { ProjectsRepository } from '@efficiently/client-api-projects';
import { ClientLoggerFacadeService } from '@efficiently/client-core-logger';
import { AppConfigService } from '@efficiently/client-core-services';
import { ShortUUIDService } from '@efficiently/client-core-utils';
import type { GetProjectsQueryVariables, GetProjectsResult } from '@efficiently/client-domain-projects';
import { getParamFromChildRouteSnapshot } from '@efficiently/client-util-helpers';

import { PROJECT_NAVIGATOR } from './constants/project-navigator.constant';
import { LastProjectStorageService } from './last-project-storage.service';
import type { ProjectNavigatorDomain } from './models/project-navigator-domain.model';
import { buildProjectNavigatorTree, type ProjectNavigatorSourceRow } from './utils/build-project-navigator-tree.util';

/** What the store holds before anything is loaded, and what a failed first load leaves it holding. */
const NO_ROWS: readonly ProjectNavigatorSourceRow[] = [];

/** The route parameter a project's short id travels in. One spelling, matched by the router and by this. */
const PROJECT_ID_ROUTE_PARAM = 'projectId';

/** The only page the navigator ever asks for — see `PROJECT_NAVIGATOR.PAGE_SIZE` for why there is one. */
const FIRST_PAGE = 1;

/**
 * The one request this store issues, pinned as a constant rather than assembled at the call site.
 *
 * `satisfies` rather than an annotation so the object keeps its literal types while still being checked
 * against the generated variables: a field the schema renames becomes a compile error here instead of a
 * silently ignored variable at runtime.
 */
const NAVIGATOR_QUERY_VARIABLES = {
	stage: ProjectStage.Active,
	pagination: {
		page: FIRST_PAGE,
		size: PROJECT_NAVIGATOR.PAGE_SIZE,
	},
} as const satisfies GetProjectsQueryVariables;

/**
 * Emitted when the server holds more projects than one page can carry. Stable by contract — it is what a
 * SIEM query filters on — and it carries counts only; see {@link ProjectNavigatorStore.warnIfTruncated}.
 */
const NAVIGATOR_TRUNCATED_EVENT = 'projects.navigator_truncated';

/** Human-readable half of the truncation record. Carries no project, no organization and no user. */
const NAVIGATOR_TRUNCATED_TEXT = 'Domains & Projects menu is showing one page of a larger project list';

/** Emitted when the one load the menu depends on failed. The user's outcome is an empty menu. */
const NAVIGATOR_LOAD_FAILED_EVENT = 'projects.navigator_load_failed';

/** The whole of what the failure record says about the failure. See {@link ProjectNavigatorStore.reportLoadFailure}. */
const NAVIGATOR_LOAD_FAILED_TEXT = 'Domains & Projects menu could not load its projects';

/** Stands in for a thrown value that is not an `Error` at all — `throw 'oops'` is legal JavaScript. */
const UNKNOWN_ERROR_NAME = 'Unknown';

/**
 * The `name` of a caught value, and nothing else from it.
 *
 * `name` rather than `constructor.name`: the latter is a minified identifier in a production bundle, so it
 * answers `t` in exactly the environment the record is read from. `ApolloError`, `TypeError` and a
 * `DOMException`'s specific name all survive minification on `name`.
 */
function toErrorName(error: unknown): string {
	return error instanceof Error ? error.name : UNKNOWN_ERROR_NAME;
}

/**
 * The Domains & Projects menu's data: the project tree it renders, the project the router is currently on,
 * and the two lookups the row menus and dialogs of phase 2 ask it for.
 *
 * **Why a signal store and not NGXS.** Everything here is one server read plus values derived from it. An
 * NGXS state would add an action, a selector and a state model for a list that is fetched once and then
 * only read, and the menu's consumers are components that want a `Signal`, not a stream. The reference for
 * the shape is `PlatformAdminTabsStore`, the other root signal store this section added.
 *
 * `providedIn: 'root'` is load-bearing rather than a default: the header title lives in the shell and the
 * routes that change the active project live under it, so reader and writer are on opposite sides of the
 * component tree and neither is an ancestor of the other. It is also what makes "loaded once" mean once
 * per session.
 *
 * **It is not an authorization gate.** Every row here came back from a query the session was already
 * entitled to run; `hasProject` answers "is this project in the menu", never "may this user see it". The
 * `canMatch` guards on the project routes remain the only gate, and nothing may read this store as one.
 */
@Injectable({ providedIn: 'root' })
export class ProjectNavigatorStore {
	private readonly projectsRepository = inject(ProjectsRepository);

	private readonly lastProjectStorage = inject(LastProjectStorageService);

	private readonly router = inject(Router);

	/**
	 * Reads BACKWARDS from the name: `to()` is short → full, `from()` is full → short. The route carries the
	 * short form, so this store only ever calls `to()` — and only after `validate()`.
	 */
	private readonly shortUuid = inject(ShortUUIDService);

	private readonly appConfig = inject(AppConfigService);

	private readonly destroyRef = inject(DestroyRef);

	/**
	 * `optional`, like {@link LastProjectStorageService}'s own injection and for the same reason: the facade
	 * comes from `provideClientLogger()` rather than from root, so a consumer that has not called it must
	 * still get a working menu instead of a `NullInjectorError` raised from a dependency it never asked
	 * about.
	 */
	private readonly logger = inject(ClientLoggerFacadeService, { optional: true });

	/**
	 * The rows exactly as the query returned them, kept private.
	 *
	 * They are the source of both derived values below, and they are the reason {@link projectName} can
	 * answer a bare `name`: the tree's labels carry the project number too, and a confirm dialog reading
	 * "Archive 12 - Riverside Tower?" is not the sentence the design asks for.
	 */
	private readonly rows = signal<readonly ProjectNavigatorSourceRow[]>(NO_ROWS);

	private readonly loading = signal(false);

	/**
	 * Whether a load has ever SUCCEEDED. It is what makes {@link load} idempotent, and it is deliberately
	 * not set on failure: a cached failure would strand the menu empty for the rest of the session, with no
	 * way back short of a reload of the page.
	 */
	private hasLoaded = false;

	/**
	 * Every addressable project's bare `name`, by id — the index behind {@link hasProject} and
	 * {@link projectName}, so neither is a scan of the row list on every row menu that opens.
	 *
	 * Rows with no `_id` are left out for the same reason the tree drops them: nothing can address them, so
	 * no caller can ask about them.
	 */
	private readonly projectNamesById: Signal<ReadonlyMap<string, string>> = computed(() => {
		const index = new Map<string, string>();

		for (const row of this.rows()) {
			if (row._id != null) {
				index.set(row._id, row.name);
			}
		}

		return index;
	});

	/**
	 * The projects grouped by domain. Empty until the first load succeeds, and empty after one that failed.
	 *
	 * `buildProjectNavigatorTree` is pure by contract, which is what lets it run inside a `computed` — the
	 * tree is rebuilt when the rows change and at no other time. The session's organization is read from
	 * the bootstrap configuration rather than from a signal because it cannot change while the tab is open;
	 * it lands before any project ever does, so a recomputation can never observe it half-set.
	 */
	public readonly tree: Signal<readonly ProjectNavigatorDomain[]> = computed(() =>
		buildProjectNavigatorTree(this.rows(), this.sessionOrgId()),
	);

	public readonly isLoading: Signal<boolean> = this.loading.asReadonly();

	/**
	 * The first project in TREE order — the first project of the first group of the first domain — or
	 * `null` while the menu has nothing to show.
	 *
	 * Tree order rather than response order on purpose: this is what the user sees at the top of the menu,
	 * and a "first project" taken off the raw response would land a redirect on a row somewhere down the
	 * list, which reads as the application choosing a project at random.
	 */
	public readonly firstProjectId: Signal<string | null> = computed(() => {
		const firstDomain = this.tree()[0];

		return firstDomain?.groups[0]?.projects[0]?.id ?? null;
	});

	/**
	 * The project the router is currently inside, in its full form, or `null` anywhere outside a project.
	 *
	 * The route parameter is EXTERNAL INPUT — anyone can type a URL — so it is validated as a short id
	 * before it is decoded. `shortToUuid` does not validate: given in-alphabet characters of the wrong
	 * length it returns a well-formed but wrong uuid, and given anything else it throws. A malformed
	 * parameter must produce neither, so it produces `null`.
	 *
	 * `toSignal` in this field initializer registers its own teardown on the root injector's `DestroyRef`,
	 * which is the correct lifetime for a root service: the subscription ends when the application does and
	 * never before, and there is nothing for a caller to unsubscribe.
	 */
	public readonly activeProjectId: Signal<string | null> = toSignal(
		this.router.events.pipe(
			filter((event): event is NavigationEnd => event instanceof NavigationEnd),
			map(() => this.routeProjectId()),
		),
		{ initialValue: this.routeProjectId() },
	);

	constructor() {
		effect(() => {
			const projectId = this.activeProjectId();

			if (projectId === null) {
				return;
			}

			untracked(() => this.lastProjectStorage.write(projectId));
		});
	}

	/**
	 * Loads the menu's projects, once per session.
	 *
	 * Safe to call from every consumer that needs the menu — a second call after a success is a no-op, and a
	 * call made while the first is still in flight issues nothing, because the request already in flight
	 * publishes into the same signals every caller is reading. A call after a FAILURE does fetch again,
	 * which is the whole point of not caching failures.
	 */
	public load(): void {
		if (this.hasLoaded || untracked(() => this.loading())) {
			return;
		}

		this.fetch();
	}

	/** Refetches unconditionally. The previous rows stay visible until the new ones land, or the old ones fail. */
	public reload(): void {
		this.fetch();
	}

	/** Whether the menu holds this project. A membership answer, never a permission one — see the class doc. */
	public hasProject(projectId: string): boolean {
		return this.projectNamesById().has(projectId);
	}

	/**
	 * The project's bare `name` — no number, no separator — or `null` when the menu does not hold it.
	 *
	 * This is the one place a project's name leaves the store, and it leaves it to a dialog or a snackbar
	 * the user is already looking at. It is never logged.
	 */
	public projectName(projectId: string): string | null {
		return this.projectNamesById().get(projectId) ?? null;
	}

	/**
	 * Issues the request and hands the result to the two paths below.
	 *
	 * Subscribed here rather than returned: the callers are components opening a menu, and a returned
	 * observable nobody subscribes is a request that never reaches the server. `takeUntilDestroyed` bounds
	 * it to the root injector's lifetime — the query completes on its own, so this covers only the case of
	 * the application being torn down mid-flight.
	 */
	private fetch(): void {
		this.loading.set(true);

		this.projectsRepository
			.getProjects(NAVIGATOR_QUERY_VARIABLES)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (result: GetProjectsResult) => this.acceptResult(result),
				error: (error: unknown) => this.reportLoadFailure(error),
			});
	}

	private acceptResult(result: GetProjectsResult): void {
		this.hasLoaded = true;
		this.rows.set(result.projects);
		this.loading.set(false);
		this.warnIfTruncated(result.pagination);
	}

	/**
	 * Reports a server holding more projects than came back.
	 *
	 * The navigator asks for one page and renders all of it; there is no "next page" anywhere in the design,
	 * so a project past the ceiling is simply absent, and absent reads to the user as "that project does not
	 * exist". This is the record that turns a silent truncation into something a reader can find.
	 *
	 * The record is `{ total, size }` and nothing else: two counts, no project name, no organization, no
	 * user. Both come from the server's own echo of the pagination, so the comparison is against what was
	 * actually served rather than against what was asked for.
	 */
	private warnIfTruncated(pagination: GetProjectsResult['pagination']): void {
		if (pagination === null) {
			return;
		}

		const { total, size } = pagination;

		if (total === null || total <= size) {
			return;
		}

		this.logger?.warn(NAVIGATOR_TRUNCATED_EVENT, NAVIGATOR_TRUNCATED_TEXT, { total, size });
	}

	/**
	 * Reports a failed load and leaves the store usable.
	 *
	 * Nothing is rethrown: the caller is a menu, not a transaction, and the downstream guard is specified to
	 * render the empty state on a failed load rather than redirect. `isLoading` returns to false and the
	 * rows are left as they were — empty on a first load, the previous page's on a failed {@link reload}.
	 *
	 * The record carries the error's NAME and not its message. This is an Apollo error: `ApolloError`
	 * stringifies the server's own error text, and a network-layer failure embeds the request URL. The
	 * client log sanitiser masks by key, `errorMessage` is not on its denylist, and every signed-in session
	 * ships these records — so the message is excluded here rather than trimmed downstream. Same reasoning
	 * and same shape as {@link LastProjectStorageService}'s own failure record.
	 */
	private reportLoadFailure(error: unknown): void {
		this.loading.set(false);

		this.logger?.error(NAVIGATOR_LOAD_FAILED_EVENT, NAVIGATOR_LOAD_FAILED_TEXT, {
			errorName: toErrorName(error),
		});
	}

	/**
	 * The deepest activated route's `projectId`, decoded, or `null`.
	 *
	 * `getParamFromChildRouteSnapshot` walks `firstChild` DOWNWARD from the root, which is the direction
	 * this needs: the parameter sits on a child of whatever route is currently matched, and a walk upward
	 * from a leaf would answer for the route the user just left.
	 */
	private routeProjectId(): string | null {
		const shortId = getParamFromChildRouteSnapshot<string>(
			this.router.routerState.snapshot.root,
			PROJECT_ID_ROUTE_PARAM,
		);

		if (shortId === null || !this.shortUuid.validate(shortId)) {
			return null;
		}

		return this.shortUuid.to(shortId);
	}

	/**
	 * The organization this session belongs to, or `null` when there is not one to have.
	 *
	 * `AppConfigService.config` is a getter over a field the bootstrap populates behind a definite-assignment
	 * assertion, so a read before it lands is a `TypeError` rather than a missing id — and root services are
	 * constructed while the application initializer is still pending. Caught here, exactly as
	 * {@link LastProjectStorageService} catches it, because "no organization" is a legitimate answer the tree
	 * already handles: with no organization in hand, no project is owned by it.
	 */
	private sessionOrgId(): string | null {
		try {
			return this.appConfig.config.org?._id ?? null;
		} catch {
			return null;
		}
	}
}
